import {ChangeDetectionStrategy, Component, Input, OnInit} from '@angular/core';
import {AppSettingsService} from 'src/app/services/app-settings/app-settings.service';
import {StaticValuesUtils} from '../../utils/static-values/static-values.utils';
import {Cart, Identifier, Passenger, Price, Segment} from '../models/cart/cart';
import {Item} from '../models/domain/item';
import {CurrencyService} from '../../services/currency/currency.service';
import {PriceFormatterPipe} from '../../utils/priceFormatter.pipe';
import {ProductUtilsService} from '../../services/product-utils.service';
import {ActivatedRoute} from '@angular/router';
import {ApiService} from '../../services/api/api.service';

@Component({
    selector: 'cart-manager',
    templateUrl: './cart-manager.component.html',
    styleUrls: ['./cart-manager.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [PriceFormatterPipe]
})
export class CartManagerComponent implements OnInit {
    @Input() item: Partial<Item>;
    @Input() cityGid: string;
    @Input() bookingCurrency?: string;

    product: string;
    xClient: string;
    cartData: Cart;
    viewType: string;

    private readonly _product_type = 'TRANSFER';

    constructor(private readonly appSettingsService: AppSettingsService,
                private readonly productUtilsService: ProductUtilsService,
                private readonly currencyService: CurrencyService,
                private readonly activatedRoute: ActivatedRoute,
                private readonly apiService: ApiService,) {
    }

    ngOnInit(): void {
        this.product = this.appSettingsService.getProduct();
        this.xClient = this.appSettingsService.getAppName();
        this.viewType = this.getViewType();
        this.cartData = this.getCartData();
    }

    getViewType(): string {
        return StaticValuesUtils.isWebview ? 'WEB_VIEW' : 'WEB';
    }

    getCartData(): Cart {
        const item = this.item;
        const currency = this.currencyService.selectedCurrency.value;
        const price: Price = this.createPrice(item, currency);
        const distribution = this.getDistribution();
        const fromDate = this.getFromDate();

        let cartData: Cart = {
            from_date: this.getFormattedDate(fromDate),
            tracking_id: item.trackingId,
            product_type: this._product_type,
            service_type: item.transferServiceType,
            category: item.transferCategory,
            url: this.productUtilsService.getUrl(item),
            passengers: this.createPassengers(distribution),
            images: item.image?.key ? [item.image.key] : [],
            segments: [this.getSegment(item.outwardSegment)],
            characteristics: this.productUtilsService.getCharacteristics(item),
            details: this.productUtilsService.getDetails(item),
            origin_gid: this.cityGid,
            destination_gid: this.cityGid,
            vehicle_type: item.vehicle,
            tags: item.tags || [],
            price: price,
            identifier: this.createIdentifier(item)
        };

        if(item.returnSegment) cartData.segments.push(this.getSegment(item.returnSegment));
        if(item.serviceType) cartData.sale_drivers = this.productUtilsService.getSaleDriver(item);
        if(this.bookingCurrency) cartData.booking_currency = this.bookingCurrency;

        return cartData;
    }

    private getDistribution(): string {
        let distribution = null;
        this.activatedRoute.paramMap.subscribe(params => {
            distribution = params.get('distribution');
        });
        return distribution;
    }

    private getFromDate(): string {
        let fromDate = null;
        this.activatedRoute.paramMap.subscribe(params => {
            fromDate = params.get('fromDate');
        });
        return fromDate;
    }

    private getFormattedDate(date: string): string {
        // Conversion from DD-MM-YYYY to YYYY-MM-DD
        return date.split('-').reverse().join('-');
    }

    private createPrice(item: Partial<Item>, currency: string): Price {
        return {
            currency: currency,
            gross: item.prices[currency].totalPrice,
        };
    }

    private createIdentifier(item: Partial<Item>): Identifier {
        let identifier = JSON.parse(StaticValuesUtils.getItemIdentifierConfiguration());

        identifier.item_hash = item.itemHash;
        return identifier;
    }

    private createPassengers(distribution: string): Passenger {
        if (!distribution) return null;
        let passengers = this.apiService.getDistributionParams(distribution);
        return {
            adults: parseInt(passengers.adults),
            minors: passengers.children != '' ? passengers.children.split(',').length : 0,
        };
    }

    private getSegment(segment: any): Segment {
        const pickupDateTime = segment.pickupDatetime ? this.productUtilsService.formatDateToISOWithoutTimezone(segment.pickupDatetime.toString()) : undefined;
        return {
            date: pickupDateTime,
            duration: segment.duration,
            origin: {
                type: segment.origin.type,
                code: segment.origin.code,
                name: segment.origin.name
            },
            destination: {
                type: segment.destination.type,
                code: segment.destination.code,
                name: segment.destination.name
            },
        maximum_stop_number: segment.maximumStopNumber

        };
    }
}